import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

// Global search palette (⌘K / Ctrl+K): find a function ("ATS > Candidates",
// "Campaigns") or a name, and see everywhere it appears with a breadcrumb —
// Projects > <project> > Candidate > <name>, ATS > Client > <name>, etc.
// Replaces the old global Data tabs.

interface Row {
  key: string;
  crumb: string[];     // breadcrumb parts, last part is the entity name
  sub?: string;        // secondary line
  path: string;
}

interface SearchData {
  candidates: { id: number; name: string; current_position: string | null; current_company: string | null; project_id: number; project_name: string }[];
  contacts: { id: number; name: string; job_title: string | null; org: string | null; project_id: number; project_name: string }[];
  companies: { id: number; company_name: string; industry: string | null; project_id: number; project_name: string }[];
  projects: { id: number; name: string }[];
}

const EMPTY: SearchData = { candidates: [], contacts: [], companies: [], projects: [] };

const GlobalSearch: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [data, setData] = useState<SearchData>(EMPTY);
  const [atsPeople, setAtsPeople] = useState<{ id: number; name: string; title: string | null; company: string | null; is_candidate: boolean; is_contact: boolean }[]>([]);
  const [atsClients, setAtsClients] = useState<{ id: number; name: string }[]>([]);
  const [sel, setSel] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const isUser1 = user?.id === 1;
  const isAdmin = user?.role === 'admin';

  const navIndex = useMemo(() => {
    const entries: { label: string; path: string; keywords: string }[] = [
      { label: 'Dashboard', path: '/', keywords: 'home overview' },
      { label: 'Projects', path: '/projects', keywords: 'maps markets' },
      { label: 'Campaigns', path: '/campaigns', keywords: 'outreach lemlist sourcewhale delivery bd' },
      { label: 'Data Enrichment', path: '/enrichment', keywords: 'csv enrich upload' },
      { label: 'Learn', path: '/training', keywords: 'training help' },
      { label: 'Team', path: '/team', keywords: 'members invite' },
      { label: 'Settings', path: '/settings', keywords: 'account billing subscription integrations' },
    ];
    if (isUser1) {
      entries.push(
        { label: 'Search', path: '/search', keywords: 'universe people companies' },
        { label: 'V2 Lab (datasets)', path: '/v2lab', keywords: 'datasets lab sample upload census mapping' },
        { label: 'Contact Search', path: '/contact-search', keywords: 'hiring managers contacts universe' },
        { label: 'ATS > Overview', path: '/ats', keywords: 'crm dashboard kpis targets' },
        { label: 'ATS > Reports', path: '/ats/reports', keywords: 'crm funnel conversion time in stage rejections analytics' },
        { label: 'ATS > Candidates', path: '/ats/candidates', keywords: 'crm people delivery' },
        { label: 'ATS > Roles', path: '/ats/roles', keywords: 'crm jobs vacancies pipelines' },
        { label: 'ATS > Placements', path: '/ats/placements', keywords: 'crm fees revenue invoices' },
        { label: 'CRM > Clients', path: '/ats/clients', keywords: 'ats accounts businesses bd' },
        { label: 'CRM > Contacts', path: '/ats/contacts', keywords: 'ats people hiring managers bd' },
        { label: 'CRM > Deals', path: '/ats/deals', keywords: 'ats pipeline mrr revenue subscriptions opportunities bd' },
        { label: 'ATS > Inbox', path: '/ats/inbox', keywords: 'crm email workspace' },
        { label: 'ATS > Tasks', path: '/ats/tasks', keywords: 'crm todos reminders workspace' },
        { label: 'ATS > Notes', path: '/ats/notes', keywords: 'crm calls meetings logged workspace' },
        { label: 'ATS > Calendar', path: '/ats/calendar', keywords: 'crm meetings interviews workspace' },
        { label: 'Map Data', path: '/mapdata', keywords: 'territories' },
        { label: 'Prospect Pages', path: '/prospect-pages', keywords: 'pitch decks previews' },
      );
    }
    if (isAdmin) {
      entries.push(
        { label: 'Admin > CRM', path: '/admin/crm', keywords: 'sales prospects demos deals internal' },
        { label: 'Admin > Command Center', path: '/admin/command-center', keywords: 'ops' },
      );
    }
    return entries;
  }, [isUser1, isAdmin]);

  useEffect(() => {
    if (open) {
      setQ(''); setSel(0); setData(EMPTY); setAtsPeople([]); setAtsClients([]);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !token) return;
    const term = q.trim();
    if (term.length < 2) { setData(EMPTY); setAtsPeople([]); setAtsClients([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const jobs: Promise<any>[] = [
          fetch(`/api/global/search?q=${encodeURIComponent(term)}`, { headers }).then((r) => (r.ok ? r.json() : EMPTY)),
        ];
        if (isUser1) {
          jobs.push(
            fetch(`/api/ats/people?search=${encodeURIComponent(term)}`, { headers }).then((r) => (r.ok ? r.json() : { people: [] })).catch(() => ({ people: [] })),
            fetch(`/api/ats/clients?search=${encodeURIComponent(term)}`, { headers }).then((r) => (r.ok ? r.json() : { clients: [] })).catch(() => ({ clients: [] })),
          );
        }
        const [global, ats, cl] = await Promise.all(jobs);
        setData(global || EMPTY);
        setAtsPeople(ats?.people?.slice(0, 8) || []);
        setAtsClients(cl?.clients?.slice(0, 5) || []);
        setSel(0);
      } catch { /* leave previous results */ }
      setLoading(false);
    }, 220);
    return () => clearTimeout(t);
  }, [q, open, token, isUser1]);

  const rows: Row[] = useMemo(() => {
    const term = q.trim().toLowerCase();
    const out: Row[] = [];

    const navMatches = term
      ? navIndex.filter((n) => `${n.label} ${n.keywords}`.toLowerCase().includes(term))
      : navIndex;
    for (const n of navMatches.slice(0, term ? 5 : 12)) {
      out.push({ key: `nav:${n.path}`, crumb: n.label.split(' > '), path: n.path });
    }

    for (const p of atsPeople) {
      const kind = p.is_contact && !p.is_candidate ? 'Contact' : 'Candidate';
      out.push({ key: `atsp:${p.id}`, crumb: ['ATS', kind, p.name], sub: [p.title, p.company].filter(Boolean).join(' · '), path: `/ats/people/${p.id}` });
    }
    for (const c of atsClients) {
      out.push({ key: `atsc:${c.id}`, crumb: ['ATS', 'Client', c.name], path: `/ats/clients/${c.id}` });
    }

    for (const p of data.projects) {
      out.push({ key: `proj:${p.id}`, crumb: ['Projects', p.name], path: `/project/${p.id}` });
    }
    for (const c of data.candidates) {
      out.push({ key: `cand:${c.id}`, crumb: ['Projects', c.project_name, 'Candidate', c.name], sub: [c.current_position, c.current_company].filter(Boolean).join(' · '), path: `/project/${c.project_id}` });
    }
    for (const h of data.contacts) {
      out.push({ key: `hm:${h.id}`, crumb: ['Projects', h.project_name, 'Contact', h.name], sub: [h.job_title, h.org].filter(Boolean).join(' · '), path: `/project/${h.project_id}` });
    }
    for (const c of data.companies) {
      out.push({ key: `comp:${c.id}`, crumb: ['Projects', c.project_name, 'Company', c.company_name], sub: c.industry || undefined, path: `/project/${c.project_id}` });
    }
    return out;
  }, [q, navIndex, data, atsPeople, atsClients]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  if (!open) return null;

  const go = (row: Row) => { onClose(); navigate(row.path); };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter' && rows[sel]) { e.preventDefault(); go(rows[sel]); }
    else if (e.key === 'Escape') { onClose(); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/30 flex items-start justify-center pt-[12vh] px-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="flex items-center gap-2.5 px-4 border-b border-gray-100">
          <svg className="w-4.5 h-4.5 w-[18px] h-[18px] text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search people, companies, projects — or jump to a section…"
            className="flex-1 py-3.5 text-sm focus:outline-none placeholder:text-gray-400"
          />
          {loading && <span className="w-3.5 h-3.5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin flex-shrink-0" />}
          <kbd className="text-[10px] text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">esc</kbd>
        </div>
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1.5">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">
              {q.trim().length < 2 ? 'Type to search…' : loading ? 'Searching…' : 'No matches.'}
            </p>
          ) : rows.map((row, i) => (
            <button
              key={row.key}
              data-selected={i === sel}
              onClick={() => go(row)}
              onMouseMove={() => setSel(i)}
              className={`w-full text-left px-4 py-2 flex items-center gap-2 ${i === sel ? 'bg-blue-50' : ''}`}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm truncate">
                  {row.crumb.slice(0, -1).map((part, j) => (
                    <span key={j} className="text-gray-400">{part} <span className="text-gray-300">›</span> </span>
                  ))}
                  <span className={`font-medium ${i === sel ? 'text-blue-700' : 'text-gray-900'}`}>{row.crumb[row.crumb.length - 1]}</span>
                </span>
                {row.sub && <span className="block text-[11px] text-gray-400 truncate">{row.sub}</span>}
              </span>
              {i === sel && <kbd className="text-[10px] text-gray-400 border border-gray-200 rounded px-1 py-0.5 flex-shrink-0">↵</kbd>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
